import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';

/**
 * SearchOrder
 * -----------------------------------------------------------------------------
 * Allows shoppers to look up an existing order (Order ID + checkout email).
 *
 * When a matching order is found we *immediately* show your existing
 * <OrderSuccess/> page by pushing the appropriate query‑string param – no extra
 * fetches or duplicated UI. If the lookup fails we surface a friendly error.
 */
export default function SearchOrder() {
  const [orderId, setOrderId]   = useState('');
  const [email, setEmail]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [redirectId, setRedirectId] = useState(null); // 👉 triggers <Navigate/>

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!orderId.trim() || !email.trim()) {
      setError('Please enter both your order ID and email.');
      return;
    }

    setLoading(true);

    try {
      const lookupUrl = `/orders/lookup?orderId=${encodeURIComponent(orderId.trim())}&email=${encodeURIComponent(email.trim())}`;
      const res       = await fetch(lookupUrl);

      if (!res.ok) {
        throw new Error('Order not found. Please double‑check your details.');
      }

      // ✅ Everything checks out – flip the switch so we render <Navigate/>
      setRedirectId(orderId.trim());

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ——————————————————————————————————————————————————————————
  // 1️⃣  Once we have a confirmed order we immediately switch to the
  //     OrderSuccess route (same component you already built):
  if (redirectId) {
    return (
      <Navigate
        to={`/order-confirmation/success?order_id=${encodeURIComponent(redirectId)}`}
        replace
      />
    );
  }

  // 2️⃣  Otherwise we stay on the lookup form:
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight={700} textAlign="center" mb={3}>
          Find Your Order
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Order ID"
            fullWidth
            margin="normal"
            value={orderId}
            onChange={e => setOrderId(e.target.value)}
            autoComplete="off"
          />

          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={e => setEmail(e.target.value)}
            autoComplete="email"
          />

          {error && (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          )}

          <Box position="relative" mt={2}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ fontWeight: 600 }}
            >
              Search
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  mt: '-12px',
                  ml: '-12px',
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
